/**
 * Client-side registration validation (mirrors server rules for fast feedback).
 */

const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'example.com',
  'example.org',
  'test.com',
  'mailinator.com',
  'tempmail.com',
  'guerrillamail.com',
  'yopmail.com',
  '10minutemail.com',
]);

const BLOCKED_EMAIL_LOCALS = new Set(['test', 'admin', 'user', 'abc', 'aaa', 'xxx', 'fake', 'none', 'no', 'na']);

function countLetters(value) {
  return (String(value || '').match(/[\p{L}\p{M}]/gu) || []).length;
}

function isRepeatedChar(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  return s.split('').every((ch) => ch === s[0]);
}

function isRepeatedDigits(value) {
  return /^(\d)\1+$/.test(String(value || ''));
}

function isSequentialDigits(value) {
  const s = String(value || '');
  if (s.length < 6) return false;
  const asc = '012345678901234567890';
  const desc = '98765432109876543210';
  return asc.includes(s) || desc.includes(s);
}

function validatePersonName(name, language, { minLetters = 2, minLength = 2, fieldEn = 'Name', fieldAr = 'الاسم' } = {}) {
  const s = String(name || '').trim().replace(/\s+/g, ' ');
  if (!s || s.length < minLength) {
    return language === 'ar'
      ? `${fieldAr} يجب أن يكون ${minLength} أحرف على الأقل`
      : `${fieldEn} must be at least ${minLength} characters`;
  }
  if (/^\d+$/.test(s)) {
    return language === 'ar' ? `${fieldAr} لا يمكن أن يكون أرقاماً فقط` : `${fieldEn} cannot be numbers only`;
  }
  if (countLetters(s) < minLetters) {
    return language === 'ar'
      ? `${fieldAr} يجب أن يحتوي على ${minLetters} أحرف على الأقل`
      : `${fieldEn} must include at least ${minLetters} letters`;
  }
  if (isRepeatedChar(s)) {
    return language === 'ar' ? `يرجى إدخال ${fieldAr} صحيح` : `Please enter a valid ${fieldEn.toLowerCase()}`;
  }
  return '';
}

export function validateRegistrationName(name, language) {
  return validatePersonName(name, language);
}

export function validateRegistrationBusiness(businessName, language) {
  return validatePersonName(businessName, language, {
    minLetters: 2,
    minLength: 3,
    fieldEn: 'Business name',
    fieldAr: 'اسم المنشأة',
  });
}

export function validateRegistrationEmail(email, language) {
  const s = String(email || '').trim().toLowerCase();
  if (!s) {
    return language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
  }
  const basic = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!basic.test(s)) {
    return language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address';
  }
  const [local, domain] = s.split('@');
  if (!local || local.length < 2 || domain.length < 4) {
    return language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email address';
  }
  if (BLOCKED_EMAIL_LOCALS.has(local) || /^\d+$/.test(local)) {
    return language === 'ar'
      ? 'يرجى استخدام بريد إلكتروني حقيقي يمكنك الوصول إليه'
      : 'Please use a real email address you can access';
  }
  if (DISPOSABLE_EMAIL_DOMAINS.has(domain)) {
    return language === 'ar' ? 'عناوين البريد المؤقت غير مسموحة' : 'Disposable email addresses are not allowed';
  }
  return '';
}

export function validateRegistrationPhoneLocal(localNumber, dialCode, language) {
  const normalized = String(localNumber || '').replace(/[\s\-\(\)]/g, '');
  if (!normalized) {
    return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
  }
  if (!/^\d+$/.test(normalized)) {
    return language === 'ar' ? 'رقم الهاتف يجب أن يحتوي على أرقام فقط' : 'Phone number must contain only digits';
  }
  if (normalized.length < 8) {
    return language === 'ar' ? 'رقم الهاتف قصير جداً' : 'Phone number is too short';
  }
  const fullNumber = String(dialCode || '') + normalized;
  if (fullNumber.length > 15) {
    return language === 'ar' ? 'رقم الهاتف طويل جداً' : 'Phone number is too long';
  }
  if (isRepeatedDigits(fullNumber) || isSequentialDigits(fullNumber)) {
    return language === 'ar' ? 'يرجى إدخال رقم جوال حقيقي' : 'Please enter a real mobile number';
  }
  if (String(dialCode) === '966' && !/^5\d{8}$/.test(normalized)) {
    return language === 'ar' ? 'أدخل رقم جوال سعودي صحيح (يبدأ بـ 5)' : 'Enter a valid Saudi mobile number (starts with 5)';
  }
  if (String(dialCode) === '971' && !/^5\d{8}$/.test(normalized)) {
    return language === 'ar' ? 'أدخل رقم جوال إماراتي صحيح' : 'Enter a valid UAE mobile number';
  }
  if (String(dialCode) === '965' && !/^[569]\d{7}$/.test(normalized)) {
    return language === 'ar' ? 'أدخل رقم جوال كويتي صحيح' : 'Enter a valid Kuwait mobile number';
  }
  return '';
}
